/*
 * Product Images catalog page.
 *
 * - Loads each category's products from the public /collections/<handle>/products.json
 *   endpoint (no 50-product Liquid cap), de-duplicates so each product appears
 *   once across the categories, and renders cards client-side.
 * - Opens full-size originals in the theme's bundled PhotoSwipe lightbox.
 * - Zips originals client-side with JSZip for the "Download all" buttons.
 * - Provides a ⌘K search bar that filters the products on the page.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-product-images-root]');
  if (!root) return;

  var JSZIP_URL = root.getAttribute('data-jszip-url');
  var PHOTOSWIPE_URL = root.getAttribute('data-photoswipe-url');
  var CONCURRENCY = 4;

  /* ---------------------------------------------------------------- loaders */

  var scriptCache = {};
  function loadScript(url) {
    if (scriptCache[url]) return scriptCache[url];
    scriptCache[url] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('Failed to load ' + url)); };
      document.head.appendChild(s);
    });
    return scriptCache[url];
  }

  var jszipPromise = null;
  function loadJSZip() {
    if (!jszipPromise) jszipPromise = loadScript(JSZIP_URL).then(function () { return window.JSZip; });
    return jszipPromise;
  }

  var photoswipePromise = null;
  function loadPhotoSwipe() {
    if (!photoswipePromise) photoswipePromise = loadScript(PHOTOSWIPE_URL).then(function () { return window.themePhotoswipe; });
    return photoswipePromise;
  }

  /* ------------------------------------------------------------------ utils */

  function sanitize(str) {
    return String(str == null ? '' : str)
      .trim()
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'untitled';
  }

  function extFromUrl(url) {
    var clean = String(url).split('?')[0];
    var m = clean.match(/\.(jpe?g|png|gif|webp|avif|bmp|tiff?)$/i);
    return m ? m[0].toLowerCase() : '.jpg';
  }

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function thumbsIn(el) {
    return Array.prototype.slice.call(el.querySelectorAll('[data-pi-thumb]'));
  }

  // Insert a Shopify CDN size suffix (e.g. _300x) before the file extension.
  function sizedUrl(src, suffix) {
    var query = '';
    var q = src.indexOf('?');
    if (q !== -1) { query = src.slice(q); src = src.slice(0, q); }
    return src.replace(/(\.(jpe?g|png|gif|webp|avif))$/i, suffix + '$1') + query;
  }

  // Run promise-returning task functions with bounded concurrency.
  function runPool(tasks, concurrency, onProgress) {
    return new Promise(function (resolve) {
      var i = 0, done = 0, errors = [];
      if (!tasks.length) { resolve({ errors: errors }); return; }
      function next() {
        if (i >= tasks.length) return;
        var task = tasks[i++];
        task()
          .catch(function (err) { errors.push(err); })
          .then(function () {
            done++;
            if (onProgress) onProgress(done, tasks.length);
            if (done >= tasks.length) resolve({ errors: errors });
            else next();
          });
      }
      for (var k = 0; k < Math.min(concurrency, tasks.length); k++) next();
    });
  }

  /* ----------------------------------------------------- fetch + build cards */

  // Fetch every page of a collection's products (public Ajax endpoint).
  function fetchAllProducts(handle) {
    var all = [];
    function page(n) {
      return fetch('/collections/' + handle + '/products.json?limit=250&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (data) {
          var products = (data && data.products) || [];
          all = all.concat(products);
          return products.length === 250 ? page(n + 1) : all;
        });
    }
    return page(1).catch(function () { return all; });
  }

  function buildCard(product) {
    var images = (product.images || []).filter(function (img) { return img && img.src; });

    // Collect every unique, non-empty variant SKU; keep an id->sku map so each
    // image can be named after the specific variant it belongs to.
    var variantSkuById = {};
    var skus = [];
    (product.variants || []).forEach(function (v) {
      if (!v) return;
      if (v.id != null) variantSkuById[v.id] = v.sku || '';
      if (v.sku && skus.indexOf(v.sku) === -1) skus.push(v.sku);
    });

    var card = document.createElement('article');
    card.className = 'pi-card';
    card.setAttribute('data-pi-product', '');
    card.setAttribute('data-product-handle', product.handle || '');
    card.setAttribute('data-search', (String(product.title || '') + ' ' + skus.join(' ')).toLowerCase());

    var head = document.createElement('header');
    head.className = 'pi-card__head';

    var meta = document.createElement('div');
    meta.className = 'pi-card__meta';
    var title = document.createElement('h3');
    title.className = 'pi-card__title';
    title.textContent = product.title || '';
    meta.appendChild(title);

    if (skus.length) {
      // SKUs collapsed behind a toggle (some products have 30+ variants).
      var skuToggle = document.createElement('button');
      skuToggle.type = 'button';
      skuToggle.className = 'pi-sku-toggle';
      skuToggle.setAttribute('data-pi-sku-toggle', '');
      skuToggle.setAttribute('aria-expanded', 'false');
      skuToggle.setAttribute('data-count', String(skus.length));
      skuToggle.textContent = 'Show SKUs (' + skus.length + ')';

      var skuList = document.createElement('div');
      skuList.className = 'pi-sku-list';
      skuList.setAttribute('data-pi-sku-list', '');
      skuList.hidden = true;
      skuList.textContent = skus.join(', ');

      meta.appendChild(skuToggle);
      meta.appendChild(skuList);
    } else {
      var noSku = document.createElement('span');
      noSku.className = 'pi-card__sku';
      noSku.textContent = 'No SKU';
      meta.appendChild(noSku);
    }
    head.appendChild(meta);

    if (images.length) {
      var dl = document.createElement('button');
      dl.type = 'button';
      dl.className = 'pi-btn';
      dl.setAttribute('data-pi-download-product', '');
      var dlLabel = document.createElement('span');
      dlLabel.setAttribute('data-pi-btn-label', '');
      dlLabel.textContent = 'Download all (' + images.length + ')';
      dl.appendChild(dlLabel);
      head.appendChild(dl);
    }
    card.appendChild(head);

    if (images.length) {
      var grid = document.createElement('div');
      grid.className = 'pi-card__thumbs';
      images.forEach(function (img, i) {
        // Name the file after the variant SKU when the image maps to exactly one.
        var imgSkus = [];
        (img.variant_ids || []).forEach(function (id) {
          var s = variantSkuById[id];
          if (s && imgSkus.indexOf(s) === -1) imgSkus.push(s);
        });
        var imgSku = imgSkus.length === 1 ? imgSkus[0] : '';

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pi-thumb';
        btn.setAttribute('data-pi-thumb', '');
        btn.setAttribute('data-image-id', img.id != null ? String(img.id) : String(i));
        btn.setAttribute('data-full', img.src);
        btn.setAttribute('data-w', img.width || 0);
        btn.setAttribute('data-h', img.height || 0);
        btn.setAttribute('data-sku', imgSku);
        btn.setAttribute('aria-label', 'View ' + (product.title || 'product') + ' image ' + (i + 1));

        var im = document.createElement('img');
        im.className = 'pi-thumb__img';
        im.loading = 'lazy';
        im.src = sizedUrl(img.src, '_300x');
        im.srcset = sizedUrl(img.src, '_300x') + ' 1x, ' + sizedUrl(img.src, '_600x') + ' 2x';
        im.width = img.width || 300;
        im.height = img.height || 300;
        im.alt = product.title || '';
        btn.appendChild(im);
        grid.appendChild(btn);
      });
      card.appendChild(grid);
    } else {
      var none = document.createElement('p');
      none.className = 'pi-card__noimg';
      none.textContent = 'No images';
      card.appendChild(none);
    }

    return card;
  }

  function renderGroup(container, products) {
    container.innerHTML = '';
    if (!products.length) {
      container.innerHTML = '<p class="pi-empty">No products.</p>';
      return;
    }
    var frag = document.createDocumentFragment();
    products.forEach(function (p) { frag.appendChild(buildCard(p)); });
    container.appendChild(frag);
  }

  /* --------------------------------------------------------------- lightbox */

  function openLightbox(thumb) {
    var card = thumb.closest('[data-pi-product]');
    if (!card) return;
    var thumbs = thumbsIn(card);
    loadPhotoSwipe().then(function (ps) {
      if (!ps) return;
      var items = thumbs.map(function (t) {
        return {
          src: t.getAttribute('data-full'),
          w: parseInt(t.getAttribute('data-w'), 10) || 0,
          h: parseInt(t.getAttribute('data-h'), 10) || 0
        };
      });
      var index = thumbs.indexOf(thumb);
      if (index < 0) index = 0;

      var el = document.querySelector('[data-zoom-wrapper]');
      if (!el) return;
      var PhotoSwipe = ps.PhotoSwipe.default;
      var PhotoSwipeUI = ps.PhotoSwipeUI.default;
      var options = {
        index: index,
        showHideOpacity: true,
        bgOpacity: 1,
        history: false,
        loop: true,
        closeOnVerticalDrag: true,
        shareEl: false
      };
      el.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
      var gallery = new PhotoSwipe(el, PhotoSwipeUI, items, options);
      gallery.init();
      gallery.listen('close', function () {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      });
    }).catch(function (err) {
      console.error('[product-images] lightbox failed', err);
    });
  }

  /* --------------------------------------------------------------- download */

  function filesFromCard(card, useSubfolders) {
    var handle = sanitize(card.getAttribute('data-product-handle'));
    var dir = useSubfolders ? handle + '/' : '';
    return thumbsIn(card).map(function (t, i) {
      var url = t.getAttribute('data-full');
      var sku = t.getAttribute('data-sku');
      var base = sku ? sanitize(sku) : handle;
      return { path: dir + base + '__' + pad(i + 1) + extFromUrl(url), url: url };
    });
  }

  function fetchBlob(url) {
    return fetch(url, { mode: 'cors' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.blob();
    });
  }

  function triggerDownload(blob, name) {
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(url); }, 2000);
  }

  function corsFallback(files) {
    window.alert(
      'The browser blocked bundling these images into a ZIP (cross-origin access). ' +
      'The first full-size image will open in a new tab — right-click to save it, ' +
      'or open each thumbnail and save individually.'
    );
    try { window.open(files[0].url, '_blank', 'noopener'); } catch (e) { /* popup blocked */ }
  }

  function buildZip(JSZip, label, zipName, files, firstBlob) {
    var zip = new JSZip();
    zip.file(files[0].path, firstBlob);
    if (label) label.textContent = '1/' + files.length;

    var tasks = files.slice(1).map(function (f) {
      return function () { return fetchBlob(f.url).then(function (blob) { zip.file(f.path, blob); }); };
    });

    return runPool(tasks, CONCURRENCY, function (done) {
      if (label) label.textContent = (done + 1) + '/' + files.length;
    }).then(function (poolRes) {
      if (poolRes.errors.length) {
        console.warn('[product-images] ' + poolRes.errors.length + ' image(s) failed to download', poolRes.errors);
      }
      if (label) label.textContent = 'Zipping…';
      return zip.generateAsync({ type: 'blob' }, function (meta) {
        if (label) label.textContent = 'Zipping ' + Math.round(meta.percent) + '%';
      });
    }).then(function (blob) {
      triggerDownload(blob, zipName);
    });
  }

  function zipFiles(label, zipName, files) {
    return loadJSZip().then(function (JSZip) {
      // Probe the first image: a cross-origin (CORS) block fails here, so we
      // bail to the fallback instead of producing a broken/partial zip.
      return fetchBlob(files[0].url).then(
        function (firstBlob) { return buildZip(JSZip, label, zipName, files, firstBlob); },
        function (err) {
          console.error('[product-images] image fetch blocked — using fallback', err);
          corsFallback(files);
        }
      );
    });
  }

  function prepareAndZip(button, zipName, files) {
    if (button.disabled || !files.length) return;
    var label = button.querySelector('[data-pi-btn-label]');
    var original = label ? label.textContent : '';
    button.disabled = true;
    if (label) label.textContent = 'Preparing…';

    zipFiles(label, zipName, files)
      .catch(function (err) { console.error('[product-images] download failed', err); })
      .then(function () {
        button.disabled = false;
        if (label) label.textContent = original;
      });
  }

  /* ----------------------------------------------------------------- search */

  function initSearch() {
    var input = root.querySelector('[data-pi-search-input]');
    var countEl = root.querySelector('[data-pi-search-count]');
    var noResults = root.querySelector('[data-pi-noresults]');
    var kbd = root.querySelector('[data-pi-kbd]');
    if (!input) return;

    var isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
    if (kbd && !isMac) kbd.textContent = 'Ctrl K';

    var collections = Array.prototype.slice.call(root.querySelectorAll('[data-pi-collection]'));

    function apply(query) {
      var terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
      var totalVisible = 0;

      collections.forEach(function (coll) {
        var visible = 0;
        Array.prototype.forEach.call(coll.querySelectorAll('[data-pi-product]'), function (card) {
          var hay = card.getAttribute('data-search') || '';
          var match = terms.every(function (t) { return hay.indexOf(t) !== -1; });
          card.hidden = !match;
          if (match) visible++;
        });
        // Hide a whole category only while filtering and it has no matches.
        coll.hidden = terms.length > 0 && visible === 0;
        totalVisible += visible;
      });

      if (countEl) countEl.textContent = terms.length ? (totalVisible + (totalVisible === 1 ? ' result' : ' results')) : '';
      if (noResults) noResults.hidden = !(terms.length && totalVisible === 0);
    }

    input.addEventListener('input', function () { apply(input.value); });

    document.addEventListener('keydown', function (e) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        input.focus();
        input.select();
      } else if (e.key === 'Escape' && document.activeElement === input) {
        input.value = '';
        apply('');
        input.blur();
      }
    });
  }

  /* ------------------------------------------------------------------- wire */

  root.addEventListener('click', function (e) {
    var skuToggle = e.target.closest('[data-pi-sku-toggle]');
    if (skuToggle) {
      var skuCard = skuToggle.closest('[data-pi-product]');
      var list = skuCard && skuCard.querySelector('[data-pi-sku-list]');
      if (!list) return;
      var show = list.hidden; // currently hidden -> reveal
      list.hidden = !show;
      skuToggle.setAttribute('aria-expanded', show ? 'true' : 'false');
      skuToggle.textContent = (show ? 'Hide SKUs' : 'Show SKUs') + ' (' + skuToggle.getAttribute('data-count') + ')';
      return;
    }

    var thumb = e.target.closest('[data-pi-thumb]');
    if (thumb) { openLightbox(thumb); return; }

    var prodBtn = e.target.closest('[data-pi-download-product]');
    if (prodBtn) {
      var card = prodBtn.closest('[data-pi-product]');
      if (!card) return;
      prepareAndZip(prodBtn, sanitize(card.getAttribute('data-product-handle')) + '.zip', filesFromCard(card, false));
      return;
    }

    var collBtn = e.target.closest('[data-pi-download-collection]');
    if (collBtn) {
      var coll = collBtn.closest('[data-pi-collection]');
      if (!coll) return;
      var files = [];
      Array.prototype.forEach.call(coll.querySelectorAll('[data-pi-product]'), function (c) {
        files = files.concat(filesFromCard(c, true));
      });
      prepareAndZip(collBtn, sanitize(coll.getAttribute('data-collection-label') || coll.getAttribute('data-collection-handle')) + '.zip', files);
    }
  });

  /* ------------------------------------------------------- load + dedupe */

  var groupEls = Array.prototype.slice.call(root.querySelectorAll('[data-pi-collection]'));
  Promise.all(groupEls.map(function (el) {
    return fetchAllProducts(el.getAttribute('data-collection-handle'));
  })).then(function (lists) {
    var seen = {};
    groupEls.forEach(function (el, gi) {
      var unique = lists[gi].filter(function (p) {
        if (seen[p.id]) return false;
        seen[p.id] = true;
        return true;
      });
      renderGroup(el.querySelector('[data-pi-products]'), unique);
    });
    initSearch();
  }).catch(function (err) {
    console.error('[product-images] failed to load products', err);
    groupEls.forEach(function (el) {
      var c = el.querySelector('[data-pi-products]');
      if (c) c.innerHTML = '<p class="pi-empty">Could not load products.</p>';
    });
  });
})();
